import CloseIcon from "@mui/icons-material/Close";
import CropIcon from "@mui/icons-material/Crop";
import { useState } from "react";
import { formatsHint } from "@/constants/uploadLimits";
import type { BrandingFields } from "@/context/CampaignBuilderContext";
import { useR2Upload } from "@/hooks/useR2Upload";
import type { MediaType } from "@/interfaces/uploads";
import { PillToggle } from "./PillToggle";
import { UploadCard } from "./UploadCard";

export interface CompanyLogoSectionProps {
  enabled: boolean;
  logoUrl: string;
  logoPadding: number;
  campaignId: string;
  /** R2 slot this logo uploads under — "logo" (email) or "landing-logo" (landing). */
  mediaType: MediaType;
  /** Saves into the email or landing branding slice (both share BrandingFields). */
  onChange: (v: Partial<BrandingFields>) => void;
  onCrop: () => void;
}

export function CompanyLogoSection({
  enabled,
  logoUrl,
  logoPadding,
  campaignId,
  mediaType,
  onChange,
  onCrop,
}: CompanyLogoSectionProps) {
  const [logoFileName, setLogoFileName] = useState("");
  const { upload, isUploading, uploadError } = useR2Upload();

  async function handleFile(file: File) {
    setLogoFileName(file.name);
    try {
      const url = await upload(file, mediaType, campaignId, "original");
      // A fresh upload is both the displayed image and the crop source, so seed both.
      // Cropping later overwrites `logoUrl` only, leaving the original to re-crop from.
      onChange({ logoUrl: url, logoOriginalUrl: url, logoEnabled: true });
    } catch {
      setLogoFileName("");
    }
  }

  return (
    <div className="py-4">
      <div className="flex items-center justify-between mb-3">
        <span
          className="font-lato text-[13px] font-bold text-rodeo-black uppercase"
          style={{ letterSpacing: "0.5px" }}
        >
          Company Logo
        </span>
        <div className="flex items-center gap-2">
          <span className="font-lato text-[11px] font-semibold uppercase tracking-[0.5px] text-graphite">
            Show
          </span>
          <PillToggle
            checked={enabled}
            onChange={(v) => onChange({ logoEnabled: v })}
            aria-label="Toggle company logo"
          />
        </div>
      </div>

      {enabled && (
        <>
          <p className="font-lato text-[12px] text-graphite mb-3">
            This will appear alongside the Gifts for Good logo
          </p>

          {uploadError && (
            <p className="font-lato text-[11px] text-good-wine mb-2">{uploadError}</p>
          )}
          <UploadCard
            previewUrl={logoUrl}
            onFile={handleFile}
            isUploading={isUploading}
            mediaType={mediaType}
            emptyLabel="Upload company logo"
            emptyHint={formatsHint(mediaType)}
          >
            <div
              className="flex items-center gap-3"
              style={{
                border: "1px solid var(--color-smoke)",
                borderRadius: 4,
                padding: "6px 8px",
              }}
            >
              <img
                // Plain display <img> — showing an image needs no CORS. Only CropDialog reads pixels
                // (canvas), and it uses its own crop-only cache key; adding crossOrigin here instead
                // made this thumbnail fail CORS off the base URL EmailPreview cached no-cors.
                src={logoUrl}
                alt="Company logo"
                className="object-contain"
                style={{
                  maxWidth: 80,
                  maxHeight: 36,
                  borderRadius: 4,
                  border: "1px solid var(--color-smoke)",
                  padding: 4,
                  boxSizing: "border-box",
                }}
              />
              <span className="flex-1 font-lato text-[13px] text-graphite truncate">
                {logoFileName || "Uploaded"}
              </span>
              <button
                type="button"
                onClick={() => {
                  setLogoFileName("");
                  onChange({ logoUrl: "", logoOriginalUrl: "", logoEnabled: false });
                }}
                className="text-graphite hover:text-good-wine transition-colors"
                title="Remove logo"
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </button>
            </div>
          </UploadCard>

          {logoUrl && (
            <div style={{ marginTop: 12 }}>
              <div className="flex items-center gap-2.5 mb-2.5">
                <span
                  className="font-lato text-[12px] font-semibold text-graphite"
                  style={{ minWidth: 56 }}
                >
                  Padding
                </span>
                <input
                  type="range"
                  min={0}
                  max={20}
                  value={logoPadding}
                  onChange={(e) => onChange({ logoPadding: Number(e.target.value) })}
                  className="logo-padding-slider appearance-none flex-1 h-1 bg-smoke rounded-sm outline-none"
                />
                <span
                  className="font-lato text-[12px] text-graphite"
                  style={{ minWidth: 30, textAlign: "right" }}
                >
                  {logoPadding}px
                </span>
              </div>
              <button
                type="button"
                onClick={onCrop}
                className="inline-flex items-center gap-1.5 font-lato text-[12px] font-semibold text-graphite transition-all"
                style={{
                  padding: "6px 12px",
                  border: "1px solid var(--color-smoke)",
                  borderRadius: 6,
                  background: "white",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "var(--color-dark-dust)";
                  e.currentTarget.style.color = "var(--color-dark-dust)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "var(--color-smoke)";
                  e.currentTarget.style.color = "var(--color-graphite)";
                }}
              >
                <CropIcon sx={{ fontSize: 16 }} />
                Crop Logo
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
